import { useState, useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";

// Iter186 (D04 UX fix) — route-level code splitting. Keep eager only
// the routes required by the initial paint (login, dashboard shell)
// so the initial bundle stops shipping every dashboard/page for a rep
// who just wants to check their leaderboard.
//
// Rule of thumb: any page reachable from the sidebar or a role dispatch
// that ISN'T the landing route gets lazy-loaded.

// Eager: needed before Suspense wraps the router
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Leaderboard from "@/pages/Leaderboard";
import ForgotPassword from "@/pages/ForgotPassword";
import InviteAccept from "@/pages/InviteAccept";
import SuperAdminLogin from "@/pages/superadmin/SuperAdminLogin";

// Lazy: everything else
const Teams = lazy(() => import("@/pages/Teams"));
const TeamDetail = lazy(() => import("@/pages/TeamDetail"));
const Quotas = lazy(() => import("@/pages/Quotas"));
const QuotaTracking = lazy(() => import("@/pages/quotas/QuotaTracking"));
const Commissions = lazy(() => import("@/pages/Commissions"));
const Deals = lazy(() => import("@/pages/Deals"));
const EODReports = lazy(() => import("@/pages/EODReports"));
const CallLibrary = lazy(() => import("@/pages/CallLibrary"));
const Templates = lazy(() => import("@/pages/Templates"));
const DataHealthCheck = lazy(() => import("@/pages/DataHealthCheck"));
const Settings = lazy(() => import("@/pages/Settings"));
const Analytics = lazy(() => import("@/pages/Analytics"));
const WeeklyDigest = lazy(() => import("@/pages/WeeklyDigest"));
const MarketingDashboard = lazy(() => import("@/pages/MarketingDashboard"));
const MarketingPlaceholder = lazy(() => import("@/pages/MarketingPlaceholder"));
const AdWarfare = lazy(() => import("@/pages/AdWarfare"));
const VSLFunnel = lazy(() => import("@/pages/VSLFunnel"));
const TypeformIntel = lazy(() => import("@/pages/TypeformIntel"));
const WebinarOps = lazy(() => import("@/pages/WebinarOps"));
const EmailSMS = lazy(() => import("@/pages/EmailSMS"));
const PreCallOps = lazy(() => import("@/pages/PreCallOps"));
const SalesWarfare = lazy(() => import("@/pages/SalesWarfare"));
const PostCallOps = lazy(() => import("@/pages/PostCallOps"));
const Cancellations = lazy(() => import("@/pages/Cancellations"));
const LeadIntel = lazy(() => import("@/pages/LeadIntel"));
const WarChest = lazy(() => import("@/pages/WarChest"));
const LeadFlow = lazy(() => import("@/pages/LeadFlow"));
const SetterPerformance = lazy(() => import("@/pages/SetterPerformance"));
const CloserPerformance = lazy(() => import("@/pages/CloserPerformance"));
const Nurture = lazy(() => import("@/pages/Nurture"));
const OfferIntel = lazy(() => import("@/pages/OfferIntel"));
const RevenueIntel = lazy(() => import("@/pages/RevenueIntel"));
const ClientIntel = lazy(() => import("@/pages/ClientIntel"));
const TeamIntel = lazy(() => import("@/pages/TeamIntel"));
const FunnelIntel = lazy(() => import("@/pages/FunnelIntel"));
const IntegrationsSettings = lazy(() => import("@/pages/IntegrationsSettings"));
const EodAuditLog = lazy(() => import("@/pages/EodAuditLog"));
const CoachingDashboard = lazy(() => import("@/pages/CoachingDashboard"));
const RepProfile = lazy(() => import("@/pages/RepProfile"));
const Onboarding = lazy(() => import("@/pages/Onboarding"));
const GoalsPage = lazy(() => import("@/pages/GoalsPage"));
const ReportsPage = lazy(() => import("@/pages/ReportsPage"));
const AlertsPage = lazy(() => import("@/pages/AlertsPage"));
const ChallengesPage = lazy(() => import("@/pages/ChallengesPage"));
const PrecallTipsPage = lazy(() => import("@/pages/PrecallTipsPage"));
const CustomDashboardPage = lazy(() => import("@/pages/CustomDashboardPage"));
const CloserDashboard = lazy(() => import("@/pages/CloserDashboard"));
const SetterDashboard = lazy(() => import("@/pages/SetterDashboard"));
const DialingDashboard = lazy(() => import("@/pages/DialingDashboard"));
const DMSetterDashboard = lazy(() => import("@/pages/DMSetterDashboard"));
const KoiSettings = lazy(() => import("@/pages/KoiSettings"));
const MyClipsPage = lazy(() => import("@/pages/MyClipsPage"));
const LeadsDashboard = lazy(() => import("@/pages/LeadsDashboard"));
const LeadProfile = lazy(() => import("@/pages/LeadProfile"));
const SetupChecklist = lazy(() => import("@/pages/SetupChecklist"));
const FieldCatalog = lazy(() => import("@/pages/FieldCatalog"));

// Super Admin — lazy (only super admins reach these)
const SuperAdminLayout = lazy(() => import("@/pages/superadmin/SuperAdminLayout"));
const SADashboard = lazy(() => import("@/pages/superadmin/SADashboard"));
const SAAccounts = lazy(() => import("@/pages/superadmin/SAAccounts"));
const SAAccountDetail = lazy(() => import("@/pages/superadmin/SAAccountDetail"));
const SAUsers = lazy(() => import("@/pages/superadmin/SAUsers"));
const SAActivity = lazy(() => import("@/pages/superadmin/SAActivity"));
const SAAuditLog = lazy(() => import("@/pages/superadmin/SAAuditLog"));

// Components
import AppLayout from "@/components/AppLayout";
import MarketingLayout from "@/components/MarketingLayout";
import AuthCallback from "@/components/AuthCallback";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import OfflineBanner from "@/components/OfflineBanner";
import { YoureBackModal } from "@/components/YoureBackModal";
import { ensureServiceWorker } from "@/lib/pushNotifications";

// Suspense fallback — minimal, dark, no layout shift.
function RouteFallback() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="text-gray-500 text-sm">Loading…</div>
    </div>
  );
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auth Context
import { createContext, useContext } from "react";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) {
      navigate("/login", { state: { from: location } });
    }
  }, [user, loading, navigate, location]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return <AppLayout>{children}</AppLayout>;
}

// Full-screen protected route (no AppLayout chrome) — used for the
// immersive onboarding flow.
function BareProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) {
      navigate("/login", { state: { from: location } });
    }
  }, [user, loading, navigate, location]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  if (!user) return null;
  return children;
}

// Roles that may access manager-tier pages (Team Dashboard, Manager Dashboard,
// Teams, Integrations, Analytics, Templates, Weekly Digest, Alerts config).
// Reps (closer / setter / dm_setter / rep) are silently bounced to /leaderboard.
const MANAGER_TIER_ROLES = new Set(["manager", "admin", "owner", "superadmin"]);

function ManagerRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/login", { state: { from: location } });
      return;
    }
    if (!MANAGER_TIER_ROLES.has((user.role || "").toLowerCase())) {
      // Rep tried to deeplink into a manager page — bounce them to the
      // gamified leaderboard (their primary surface).
      navigate("/leaderboard", { replace: true });
    }
  }, [user, loading, navigate, location]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  if (!user) return null;
  if (!MANAGER_TIER_ROLES.has((user.role || "").toLowerCase())) return null;

  return <AppLayout>{children}</AppLayout>;
}

function ProtectedMarketingRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) {
      navigate("/login", { state: { from: location } });
    }
  }, [user, loading, navigate, location]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  // Iter173 — When REACT_APP_MARKETING_COMING_SOON=true (set only in production
  // env), every /marketing/* route renders the "Coming Soon" placeholder
  // instead of the real dashboard. Preview keeps showing the working
  // dashboard so we can keep iterating on it while approvals process.
  const comingSoon = String(process.env.REACT_APP_MARKETING_COMING_SOON).toLowerCase() === "true";
  return (
    <MarketingLayout>
      {comingSoon ? <MarketingPlaceholder /> : children}
    </MarketingLayout>
  );
}

function RoleAwareRoot() {
  const { user, loading } = useAuth();
  const [onboardingState, setOnboardingState] = useState("loading");

  useEffect(() => {
    if (loading || !user) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/onboarding/status`, {
          credentials: "include",
        });
        if (cancelled) return;
        if (res.ok) {
          const data = await res.json();
          // Iter186 — a user who tapped Skip is NOT completed but must
          // NOT be trapped back into the onboarding wizard on every
          // login. They see the resume nudge in the top nav instead.
          // The 3-day scheduler will re-invite them exactly once by
          // push/email.
          if (data.completed || data.skipped) {
            setOnboardingState("done");
          } else {
            setOnboardingState("pending");
          }
        } else {
          setOnboardingState("done"); // fail-open, don't trap users
        }
      } catch {
        if (!cancelled) setOnboardingState("done");
      }
    })();
    return () => { cancelled = true; };
  }, [user, loading]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }
  // Unauthenticated users must hit /login BEFORE we wait on onboarding state
  // (the onboarding effect early-returns when !user, so onboardingState would
  // remain "loading" forever and trap the user on a blank screen).
  if (!user) return <Navigate to="/login" replace />;

  if (onboardingState === "loading") {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  // First-time users get the role-tailored walkthrough before they're
  // dropped into the app.
  if (onboardingState === "pending") return <Navigate to="/onboarding" replace />;

  // Reps land on the leaderboard (their gamified home). Managers / admins
  // land on the team dashboard.
  const dest = MANAGER_TIER_ROLES.has((user.role || "").toLowerCase())
    ? "/dashboard"
    : "/leaderboard";
  return <Navigate to={dest} replace />;
}

function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/login" element={<Login />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/teams" element={<ManagerRoute><Teams /></ManagerRoute>} />
      <Route path="/teams/:teamId" element={<ManagerRoute><TeamDetail /></ManagerRoute>} />
      <Route path="/leaderboard" element={<ProtectedRoute><Leaderboard /></ProtectedRoute>} />
      <Route path="/quotas" element={<ProtectedRoute><Quotas /></ProtectedRoute>} />
      <Route path="/quotas/tracking" element={<ProtectedRoute><QuotaTracking /></ProtectedRoute>} />
      <Route path="/commissions" element={<ProtectedRoute><Commissions /></ProtectedRoute>} />
      <Route path="/deals" element={<ProtectedRoute><Deals /></ProtectedRoute>} />
      <Route path="/eod" element={<ProtectedRoute><EODReports /></ProtectedRoute>} />
      <Route path="/call-library" element={<ProtectedRoute><CallLibrary /></ProtectedRoute>} />
      <Route path="/coaching" element={<ProtectedRoute><CoachingDashboard /></ProtectedRoute>} />
      <Route path="/coaching/rep/:userId" element={<ProtectedRoute><RepProfile /></ProtectedRoute>} />
      <Route path="/onboarding" element={<BareProtectedRoute><Onboarding /></BareProtectedRoute>} />
      <Route path="/goals" element={<ProtectedRoute><GoalsPage /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
      <Route path="/alerts" element={<ManagerRoute><AlertsPage /></ManagerRoute>} />
      <Route path="/challenges" element={<ProtectedRoute><ChallengesPage /></ProtectedRoute>} />
      <Route path="/precall-tips" element={<ProtectedRoute><PrecallTipsPage /></ProtectedRoute>} />
      <Route path="/my-dashboard" element={<ManagerRoute><CustomDashboardPage /></ManagerRoute>} />
      <Route path="/closer-dashboard" element={<ProtectedRoute><CloserDashboard /></ProtectedRoute>} />
      <Route path="/setter-dashboard" element={<ProtectedRoute><SetterDashboard /></ProtectedRoute>} />
      <Route path="/dialing-dashboard" element={<ProtectedRoute><DialingDashboard /></ProtectedRoute>} />
      <Route path="/outbound-dashboard" element={<ProtectedRoute><DialingDashboard /></ProtectedRoute>} />
      <Route path="/triage-dashboard" element={<ProtectedRoute><DialingDashboard /></ProtectedRoute>} />
      <Route path="/dm-setter-dashboard" element={<ProtectedRoute><DMSetterDashboard /></ProtectedRoute>} />
      <Route path="/koi-settings" element={<ProtectedRoute><KoiSettings /></ProtectedRoute>} />
      <Route path="/my-clips" element={<ProtectedRoute><MyClipsPage /></ProtectedRoute>} />
      <Route path="/leads" element={<ProtectedRoute><LeadsDashboard /></ProtectedRoute>} />
      <Route path="/leads/:leadId" element={<ProtectedRoute><LeadProfile /></ProtectedRoute>} />
      <Route path="/setup" element={<ProtectedRoute><SetupChecklist /></ProtectedRoute>} />
      <Route path="/templates" element={<ManagerRoute><Templates /></ManagerRoute>} />
      <Route path="/data-health" element={<ManagerRoute><DataHealthCheck /></ManagerRoute>} />
      <Route path="/data-health/field-catalog" element={<ManagerRoute><FieldCatalog /></ManagerRoute>} />
      <Route path="/analytics" element={<ManagerRoute><Analytics /></ManagerRoute>} />
      <Route path="/weekly-digest" element={<ManagerRoute><WeeklyDigest /></ManagerRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/integrations" element={<ManagerRoute><IntegrationsSettings /></ManagerRoute>} />
      <Route path="/eod/audit-log" element={<ManagerRoute><EodAuditLog /></ManagerRoute>} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/invite/:inviteId" element={<InviteAccept />} />
      
      {/* Marketing Dashboard Routes */}
      <Route path="/marketing" element={<ProtectedMarketingRoute><MarketingDashboard /></ProtectedMarketingRoute>} />
      <Route path="/marketing/ad-warfare" element={<ProtectedMarketingRoute><AdWarfare /></ProtectedMarketingRoute>} />
      <Route path="/marketing/vsl-funnel" element={<ProtectedMarketingRoute><VSLFunnel /></ProtectedMarketingRoute>} />
      <Route path="/marketing/typeform-intel" element={<ProtectedMarketingRoute><TypeformIntel /></ProtectedMarketingRoute>} />
      <Route path="/marketing/webinar-ops" element={<ProtectedMarketingRoute><WebinarOps /></ProtectedMarketingRoute>} />
      <Route path="/marketing/email-sms" element={<ProtectedMarketingRoute><EmailSMS /></ProtectedMarketingRoute>} />
      <Route path="/marketing/pre-call-ops" element={<ProtectedMarketingRoute><PreCallOps /></ProtectedMarketingRoute>} />
      <Route path="/marketing/sales-warfare" element={<ProtectedMarketingRoute><SalesWarfare /></ProtectedMarketingRoute>} />
      <Route path="/marketing/post-call-ops" element={<ProtectedMarketingRoute><PostCallOps /></ProtectedMarketingRoute>} />
      <Route path="/marketing/cancellations" element={<ProtectedMarketingRoute><Cancellations /></ProtectedMarketingRoute>} />
      <Route path="/marketing/lead-intel" element={<ProtectedMarketingRoute><LeadIntel /></ProtectedMarketingRoute>} />
      <Route path="/marketing/war-chest" element={<ProtectedMarketingRoute><WarChest /></ProtectedMarketingRoute>} />
      <Route path="/marketing/lead-flow" element={<ProtectedMarketingRoute><LeadFlow /></ProtectedMarketingRoute>} />
      <Route path="/marketing/setter-performance" element={<ProtectedMarketingRoute><SetterPerformance /></ProtectedMarketingRoute>} />
      <Route path="/marketing/closer-performance" element={<ProtectedMarketingRoute><CloserPerformance /></ProtectedMarketingRoute>} />
      <Route path="/marketing/nurture" element={<ProtectedMarketingRoute><Nurture /></ProtectedMarketingRoute>} />
      <Route path="/marketing/offer-intel" element={<ProtectedMarketingRoute><OfferIntel /></ProtectedMarketingRoute>} />
      <Route path="/marketing/revenue-intel" element={<ProtectedMarketingRoute><RevenueIntel /></ProtectedMarketingRoute>} />
      <Route path="/marketing/client-intel" element={<ProtectedMarketingRoute><ClientIntel /></ProtectedMarketingRoute>} />
      <Route path="/marketing/team-intel" element={<ProtectedMarketingRoute><TeamIntel /></ProtectedMarketingRoute>} />
      <Route path="/marketing/funnel-intel" element={<ProtectedMarketingRoute><FunnelIntel /></ProtectedMarketingRoute>} />
      <Route path="/marketing/integrations" element={<ProtectedMarketingRoute><IntegrationsSettings /></ProtectedMarketingRoute>} />
      
      {/* Super Admin Routes */}
      <Route path="/superadmin" element={<SuperAdminLogin />} />
      <Route path="/superadmin/*" element={<SuperAdminLayout />}>
        <Route path="dashboard" element={<SADashboard />} />
        <Route path="accounts" element={<SAAccounts />} />
        <Route path="accounts/:accountId" element={<SAAccountDetail />} />
        <Route path="users" element={<SAUsers />} />
        <Route path="activity" element={<SAActivity />} />
        <Route path="audit-log" element={<SAAuditLog />} />
      </Route>

      <Route path="/" element={<RoleAwareRoot />} />
      <Route path="*" element={<RoleAwareRoot />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, []);

  // Iter152 — register the service worker once at app boot so the user
  // can opt in to push notifications later from Settings. Failures are
  // logged but never block the app from rendering.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!("serviceWorker" in navigator)) return;
    ensureServiceWorker().catch(() => {});
  }, []);

  // Iter188 (D06) — Silent JWT refresh scheduler. Once we have a
  // logged-in user, keep the session alive by hitting /auth/refresh
  // ~5 minutes before the current token's `exp`. Iter189 polish — if
  // the login / /auth/me response gave us a server-authoritative
  // `expires_at`, schedule against that; otherwise fall back to a
  // 20h cadence (safe under the 24h TTL).
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    let timerId = null;

    async function schedule(nextInMs) {
      if (cancelled || nextInMs <= 0) return;
      timerId = setTimeout(async () => {
        try {
          const res = await fetch(`${API}/auth/refresh`, {
            method: "POST",
            credentials: "include",
          });
          if (res.ok) {
            const body = await res.json();
            const now = Date.now() / 1000;
            const remaining = (body.expires_at || (now + 23 * 3600)) - now;
            // Fire again 5 minutes BEFORE the new expiry.
            const nextMs = Math.max(60 * 1000, (remaining - 5 * 60) * 1000);
            if (!cancelled) schedule(nextMs);
          } else if (res.status === 401) {
            // Session expired — clean logout so /login reloads fresh.
            if (!cancelled) setUser(null);
          } else {
            // Transient failure — retry in 5 min.
            if (!cancelled) schedule(5 * 60 * 1000);
          }
        } catch {
          if (!cancelled) schedule(5 * 60 * 1000);
        }
      }, nextInMs);
    }

    // Iter189 (D06 polish) — use the server-authoritative expiry when
    // present so the first refresh fires ~5 minutes BEFORE the real
    // token expiry rather than at a blind 20h mark.
    const nowSec = Date.now() / 1000;
    const expiresAt = Number(user.expires_at) || 0;
    const firstMs = expiresAt > nowSec
      ? Math.max(60 * 1000, (expiresAt - nowSec - 5 * 60) * 1000)
      : 20 * 60 * 60 * 1000;
    schedule(firstMs);
    return () => { cancelled = true; if (timerId) clearTimeout(timerId); };
  }, [user]);

  const checkAuth = async () => {
    try {
      // Use XMLHttpRequest to avoid "object cannot be cloned" DOMException
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("GET", `${API}/auth/me`, true);
        xhr.withCredentials = true;
        
        xhr.onload = function() {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              resolve(JSON.parse(xhr.responseText));
            } catch (e) {
              reject(new Error("Parse error"));
            }
          } else {
            reject(new Error("Not authenticated"));
          }
        };
        
        xhr.onerror = function() {
          reject(new Error("Network error"));
        };
        
        xhr.send();
      });
      
      setUser(result);
      // Iter186 (C01) — silently sync the user's IANA timezone up to
      // the backend so the tz-aware EOD reminder scheduler stops
      // pinging reps at lunch. Only sends if we detected a real
      // timezone AND the user's stored value differs (or is missing).
      try {
        const detectedTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (detectedTz && detectedTz !== result?.timezone) {
          fetch(`${API}/users/profile`, {
            method: "PUT",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ timezone: detectedTz }),
          }).catch(() => {}); // fire-and-forget — never block auth
        }
      } catch { /* Intl absent (very old browser) — no-op */ }
      fetchAccounts();
    } catch (error) {
      // Silently fail - user not logged in
      console.log("Auth check:", error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchAccounts = async () => {
    try {
      // Use XMLHttpRequest to avoid "object cannot be cloned" DOMException
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("GET", `${API}/accounts`, true);
        xhr.withCredentials = true;
        
        xhr.onload = function() {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              resolve(JSON.parse(xhr.responseText));
            } catch (e) {
              resolve([]);
            }
          } else {
            resolve([]);
          }
        };
        
        xhr.onerror = function() {
          resolve([]);
        };
        
        xhr.send();
      });
      
      setAccounts(result);
    } catch (error) {
      console.log("Failed to fetch accounts");
    }
  };

  const login = (userData) => {
    setUser(userData);
    fetchAccounts();
  };

  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
    setAccounts([]);
  };

  const switchAccount = async (accountId) => {
    try {
      const response = await fetch(`${API}/accounts/${accountId}/switch`, {
        method: "POST",
        credentials: "include",
      });
      if (response.ok) {
        setUser({ ...user, account_id: accountId });
        window.location.reload();
      }
    } catch (error) {
      console.error("Failed to switch account:", error);
    }
  };

  // Iter223 — create a brand-new, empty business account under the same
  // login email, then switch into it and land on Onboarding to set up
  // teams/invites for the new account.
  const createAccount = async (businessName) => {
    const response = await fetch(`${API}/accounts`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ business_name: businessName }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data?.detail || "Failed to create account");
    }
    setUser({ ...user, account_id: data.account_id });
    window.location.href = "/onboarding";
    return data;
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, accounts, switchAccount, createAccount, checkAuth }}>
      <div className="App dark">
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
        <Toaster
          theme="dark"
          position="top-right"
          richColors
          closeButton
          toastOptions={{
            classNames: {
              toast: "bg-[#1A1A1A] border border-white/10 text-white",
            },
          }}
        />
        {user && <PWAInstallPrompt />}
        {user && <YoureBackModal />}
        <OfflineBanner />
      </div>
    </AuthContext.Provider>
  );
}

export default App;
